import React, { useState, useEffect } from "react";
import {
  Table,
  Pagination,
  TextInput,
  Button,
  Loader,
  Modal,
  Group,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconEdit, IconSearch, IconCheck, IconX } from "@tabler/icons-react";
import { ProjectTableProps } from "./type";
import { useNavigate } from "react-router-dom";
import { useProjectContext } from "../../../../contexts/project.context";

// SERVICE + AUTH imports
import { softDeleteContainer } from "../../../../services/adminService/createAndEditProject";
import { getAccessToken } from "../../../../services/authService";

// MSAL hook
import { useMsal } from "@azure/msal-react";

const ProjectTable = ({
  activePage,
  setPage,
  totalPages,
  searchValue,
  setSearchValue,
  projectData,
  onInactiveClick,
}: ProjectTableProps & {
  onInactiveClick?: (projectId: string) => void | Promise<void>;
}) => {
  const navigate = useNavigate();
  const { detailedStatus, fetchDetailedProjectList } = useProjectContext();

  // MSAL
  const { instance, accounts } = useMsal();

  const [projects, setProjects] = useState<any[]>([]);
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set<string>());

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  useEffect(() => {
    if (projectData) {
      const normalized = projectData.map((p: any) => ({
        ...p,
        isActive: p.IsActive ?? p.isActive,
        isDeleted: p.IsDeleted ?? p.isDeleted,
      }));

      // only show active (not deleted) in this table
      setProjects(normalized.filter((p: any) => !p.isDeleted));
    }
  }, [projectData]);

  const convertTimestamp = (timestampStr: string): string => {
    try {
      const dt = new Date(timestampStr);
      const year = dt.getFullYear().toString().slice(-2);
      const month = dt.toLocaleString("en-US", { month: "short" });
      const day = dt.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "Invalid timestamp";
    }
  };

  // Resolve a valid token using MSAL instance + account
  const resolveToken = async (): Promise<string | null> => {
    try {
      if (!instance) {
        console.warn("MSAL instance not available");
        return null;
      }
      const account = accounts && accounts.length > 0 ? accounts[0] : null;
      if (!account) {
        return null;
      }

      const token = await getAccessToken(instance, account);
      return token ?? null;
    } catch (err) {
      console.error("resolveToken error", err);
      return null;
    }
  };

  const callSoftDeleteApi = async (id: string | number) => {
    const sid = String(id);
    setLoadingIds((prev) => {
      const next = new Set(prev);
      next.add(sid);
      return next;
    });

    try {
      const token = await resolveToken();
      if (!token) {
        showNotification({
          title: "Authentication required",
          message: "Please sign in to perform this action.",
          color: "yellow",
          icon: <IconX size={16} />,
        });
        return;
      }

      const { data } = await softDeleteContainer(Number(id), token);
      const result = data;

      if (result && result.isDeleted) {
        // remove from active list
        const deletedProject =
          projects.find((p) => String(p.id) === sid) ?? { id: result.id, name: "(unknown)" };

        setProjects((prev) => prev.filter((p) => String(p.id) !== sid));

        showNotification({
          title: "Deleted",
          message: `Project "${deletedProject.name}" deleted successfully.`,
          color: "green",
          icon: <IconCheck size={16} />,
        });

        // refresh server-backed list in background
        try {
          fetchDetailedProjectList(searchValue, true, activePage, 6, false);
        } catch (e) {
          console.warn("background refresh failed", e);
        }
      } else {
        showNotification({
          title: "Delete failed",
          message: "Server returned unexpected response.",
          color: "red",
          icon: <IconX size={16} />,
        });
      }
    } catch (err) {
      console.error("Error deleting container:", err);
      showNotification({
        title: "Error",
        message: `Could not delete project: ${(err as Error).message}`,
        color: "red",
        icon: <IconX size={16} />,
      });
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(sid);
        return next;
      });
    }
  };

  const handleToggleClick = (project: any) => {
    if (loadingIds.has(String(project.id)) || project.isDeleted) return;
    setSelectedProject(project);
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedProject) return;
    setModalOpen(false);

    if (onInactiveClick) {
      try {
        const maybe = onInactiveClick(String(selectedProject.id));
        if (maybe != null && typeof (maybe as any).then === "function") {
          await (maybe as unknown as Promise<void>);
        }
      } catch (err) {
        console.error("Parent onInactiveClick failed", err);
        showNotification({
          title: "Error",
          message: `Could not delete project: ${(err as Error).message}`,
          color: "red",
          icon: <IconX size={16} />,
        });
      } finally {
        setSelectedProject(null);
      }
      return;
    }

    await callSoftDeleteApi(selectedProject.id);
    setSelectedProject(null);
  };

  const rows = projects.map((p) => (
    <tr key={p.id}>
      <td>{p.id}</td>
      <td>{p.name}</td>
      <td>{p.managerUserName}</td>
      <td>{p.clientName}</td>
      <td>{p.parentName}</td>
      <td>{p.containerTypeName}</td>
      <td>{convertTimestamp(p.createdOn)}</td>
      <td>{convertTimestamp(p.lastUpdatedOn)}</td>
      <td style={{ display: "flex", gap: 8 }}>
        <Button onClick={() => navigate(`edit/${p.id}`)} leftIcon={<IconEdit />} variant="white">
          Edit
        </Button>
        <Button
          color="red"
          size="xs"
          variant="filled"
          onClick={() => handleToggleClick(p)}
          disabled={loadingIds.has(String(p.id))}
        >
          {loadingIds.has(String(p.id)) ? <Loader size="xs" /> : "Delete"}
        </Button>
      </td>
    </tr>
  ));

  return (
    <>
      <TextInput
        icon={<IconSearch size={16} />}
        placeholder="Search Project"
        value={searchValue}
        onChange={(e) => setSearchValue(e.currentTarget.value)}
        style={{ marginBottom: 20 }}
      />

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
        {detailedStatus === "loading" ? <Loader variant="dots" /> : null}
      </div>

      <Table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Project Name</th>
            <th>Project Owner</th>
            <th>Client Type</th>
            <th>Parent Project Name</th>
            <th>Project Type</th>
            <th>Created Date</th>
            <th>Last Update Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>

      <Pagination total={totalPages} value={activePage} onChange={setPage} style={{ marginTop: 20 }} />

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={<Text weight={600}>Confirm Deletion</Text>} centered>
        <Text>Are you sure you want to delete project "{selectedProject?.name}"?</Text>
        <Group position="apart" mt="md">
          <Button variant="outline" color="gray" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={confirmDelete}>
            Delete
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export { ProjectTable };
